import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { curatorGroupsApi, type GroupConflict } from '@/api/curatorGroups';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useAuthStore } from '@/stores/auth';
import { curatorGroupsErrorText, isTakeoverConflict, takeoverConflicts } from '@/lib/curatorGroupErrors';
import { filterRoster, tabCounts, type RosterTab } from '@/lib/curatorGroupRows';
import {
  keepExisting,
  removableByGroup,
  selectAllFiltered,
  toggleSelected,
} from '@/lib/curatorGroupSelection';

import { useIsMobile } from '@/hooks/useIsMobile';
import { MTitle } from '@/components/mobile/MobileKit';

import { GroupsCard } from './GroupsCard';
import { RosterCard } from './RosterCard';
import { TakeoverDialog } from './TakeoverDialog';

/** Сколько строк рисуем сразу; «Показать ещё» добавляет столько же. */
const PAGE = 50;

/**
 * Курс подтипа: ученики, группы, раскладка (D40, договор
 * `docs/CONTRACTS/curator-groups.md` §2–§5).
 *
 * ⚠️ НАЗВАНИЕ КУРСА БЕРЁТСЯ ИЗ СПИСКА ПОДТИПОВ. Ручки «курс по id» у куратора
 * нет вовсе, и выдумывать заголовок из id нельзя: курс, которого нет в списке,
 * для куратора не существует (404 `marathon_not_found`), и экран говорит об
 * этом словами, а не показывает пустую страницу.
 *
 * ⚠️ ВЫБОР ПЕРЕЖИВАЕТ ПЕРЕЧИТЫВАНИЕ СПИСКА. После каждой пачки список читается
 * заново; гасить выбор целиком — терять работу куратора, отметившего полсотни
 * человек. Исчезнувшие записи при этом выбрасываются: их забрал кто-то другой, и
 * следующий запрос с ними упал бы целиком.
 */
export function CuratorGroupsCoursePage() {
  const { t } = useTranslation();
  const { marathonId = '' } = useParams();
  const qc = useQueryClient();
  const ask = useConfirm();
  const meId = useAuthStore((s) => s.user?.id);

  const [tab, setTab] = useState<RosterTab>('all');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set<string>());
  const [targetGroupId, setTargetGroupId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [created, setCreated] = useState(false);
  const [conflicts, setConflicts] = useState<GroupConflict[] | null>(null);
  const [conflictAgain, setConflictAgain] = useState(false);
  /** Что именно уходило в последнюю попытку — повтор с подтверждением берёт тот же список. */
  const [pendingIds, setPendingIds] = useState<string[]>([]);

  const courses = useQuery({
    queryKey: ['curator', 'subtype-courses'],
    queryFn: () => curatorGroupsApi.listSubtypeCourses(),
  });
  const groups = useQuery({
    queryKey: ['curator', 'groups', marathonId],
    queryFn: () => curatorGroupsApi.listGroups(marathonId),
    enabled: !!marathonId,
  });
  const roster = useQuery({
    queryKey: ['curator', 'roster', marathonId],
    queryFn: () => curatorGroupsApi.listStudents(marathonId),
    enabled: !!marathonId,
  });

  const course = courses.data?.find((c) => c.id === marathonId);
  const rows = useMemo(() => roster.data ?? [], [roster.data]);
  const groupList = useMemo(() => groups.data ?? [], [groups.data]);
  const myGroups = useMemo(() => groupList.filter((g) => g.mine), [groupList]);
  const myGroupIds = useMemo(() => new Set(myGroups.map((g) => g.id)), [myGroups]);

  const filtered = useMemo(() => filterRoster(rows, { tab, query, meId }), [rows, tab, query, meId]);
  const counts = useMemo(() => tabCounts(rows, meId), [rows, meId]);

  // Список перечитан: держим выбранными только тех, кто ещё в курсе.
  useEffect(() => {
    setSelected((prev) => (prev.size ? new Set(keepExisting(prev, rows)) : prev));
  }, [rows]);

  // Группа по умолчанию — первая своя; чужие в выбор не попадают вовсе (403).
  useEffect(() => {
    setTargetGroupId((prev) => (prev && myGroupIds.has(prev) ? prev : (myGroups[0]?.id ?? '')));
  }, [myGroups, myGroupIds]);

  // Смена вкладки или запроса — снова первые PAGE строк.
  useEffect(() => {
    setShown(PAGE);
  }, [tab, query]);

  const refreshLists = () => {
    qc.invalidateQueries({ queryKey: ['curator', 'roster', marathonId] });
    qc.invalidateQueries({ queryKey: ['curator', 'groups', marathonId] });
  };

  const create = useMutation({
    mutationFn: ({ name, capacity }: { name: string; capacity: number }) =>
      curatorGroupsApi.createGroup(marathonId, name, capacity),
    onSuccess: () => {
      setGroupError(null);
      setCreated(true);
      qc.invalidateQueries({ queryKey: ['curator', 'groups', marathonId] });
      // У курса меняется число групп — список марафонов тоже устарел.
      qc.invalidateQueries({ queryKey: ['curator', 'subtype-courses'] });
    },
    onError: (e) => {
      setCreated(false);
      setGroupError(curatorGroupsErrorText(e, t));
    },
  });

  const assign = useMutation({
    mutationFn: ({ ids, confirm }: { ids: string[]; confirm: boolean }) =>
      curatorGroupsApi.addMembers(targetGroupId, ids, confirm),
    onSuccess: (res) => {
      setError(null);
      setConflicts(null);
      setConflictAgain(false);
      setSelected(new Set<string>());
      setMessage(
        t('curatorGroups.assignedResult', {
          assigned: res.assigned,
          transferred: res.transferred,
          cleared: res.curator_cleared,
        }),
      );
      refreshLists();
    },
    onError: (e) => {
      setMessage(null);
      if (isTakeoverConflict(e)) {
        // Чужие ученики: показываем, у кого именно забираем, и ждём слова куратора.
        // Второй отказ подряд — значит кто-то успел раньше: окно остаётся со
        // свежим списком, а не закрывается с видом, будто всё прошло.
        setConflictAgain(conflicts !== null);
        setConflicts(takeoverConflicts(e));
        setError(null);
        return;
      }
      setConflicts(null);
      setConflictAgain(false);
      setError(curatorGroupsErrorText(e, t));
    },
  });

  const remove = useMutation({
    mutationFn: async ({ byGroup, skipped }: { byGroup: Map<string, string[]>; skipped: number }) => {
      let removed = 0;
      let cleared = 0;
      // По вызову на группу: ручка принимает только записи ЭТОЙ группы, и пачка
      // проверяется целиком — чужая запись уронила бы весь запрос (§5).
      for (const [groupId, ids] of byGroup) {
        const res = await curatorGroupsApi.removeMembers(groupId, ids);
        removed += res.removed;
        cleared += res.curator_cleared;
      }
      return { removed, cleared, skipped };
    },
    onSuccess: (res) => {
      setError(null);
      setSelected(new Set<string>());
      // ⚠️ О ПРОПУЩЕННЫХ ГОВОРИМ ВМЕСТЕ С ИТОГОМ, А НЕ ВМЕСТО НЕГО. Пока это
      // была отдельная строка, удачное удаление затирало её, и «убрано 2» из
      // трёх выбранных выглядело так, будто убрали всех.
      const skippedText = res.skipped ? ` · ${t('curatorGroups.removeSkipped', { count: res.skipped })}` : '';
      setMessage(t('curatorGroups.removedResult', { removed: res.removed, cleared: res.cleared }) + skippedText);
      refreshLists();
    },
    onError: (e) => {
      setMessage(null);
      setError(curatorGroupsErrorText(e, t));
    },
  });

  const startAssign = () => {
    const ids = [...selected];
    if (!ids.length || !targetGroupId) return;
    setPendingIds(ids);
    setConflictAgain(false);
    assign.mutate({ ids, confirm: false });
  };

  const startRemove = async () => {
    const { byGroup, skipped } = removableByGroup(selected, rows, myGroupIds);
    if (!byGroup.size) {
      setMessage(null);
      setError(t('curatorGroups.removeNothing'));
      return;
    }
    const count = [...byGroup.values()].reduce((n, ids) => n + ids.length, 0);
    if (!(await ask({ message: t('curatorGroups.confirmRemove', { count }), danger: true }))) return;
    setError(null);
    remove.mutate({ byGroup, skipped });
  };

  // Курс не в подтипах: список пришёл, а курса в нём нет. Сервер отвечает 404,
  // и «курса не существует» — единственная честная формулировка.
  const unavailable = courses.isSuccess && !course;
  const isMobile = useIsMobile();

  return (
    <div className={isMobile ? 'space-y-3' : undefined}>
      {isMobile ? (
        // Телефон (§4.9): одна шапка «назад к курсам · название курса · подтип».
        <MTitle back="/curator/groups" title={course?.title ?? t('curatorGroups.title')} subtitle={course?.subtype} />
      ) : (
        <PageHeader
          title={course?.title ?? t('curatorGroups.title')}
          subtitle={course ? course.subtype : undefined}
          actions={
            <Link to="/curator/groups">
              <Button variant="ghost" size="sm">{t('curatorGroups.back')}</Button>
            </Link>
          }
        />
      )}

      {unavailable ? (
        <Card variant="subtle" className="space-y-1 py-8 text-center text-on-surface-variant">
          <p className="font-semibold text-on-surface">{t('curatorGroups.courseUnavailable')}</p>
          <p className="text-sm">{t('curatorGroups.courseUnavailableDesc')}</p>
        </Card>
      ) : (
        <>
          <GroupsCard
            phone={isMobile}
            groups={groupList}
            loading={groups.isLoading}
            error={groupError ?? (groups.isError ? curatorGroupsErrorText(groups.error, t) : null)}
            created={created}
            creating={create.isPending}
            onCreate={(name, capacity) => {
              setCreated(false);
              create.mutate({ name, capacity });
            }}
          />

          <RosterCard
            phone={isMobile}
            rows={rows}
            filtered={filtered}
            shown={shown}
            counts={counts}
            tab={tab}
            query={query}
            selected={selected}
            myGroups={myGroups}
            targetGroupId={targetGroupId}
            loading={roster.isLoading}
            assigning={assign.isPending}
            removing={remove.isPending}
            message={message}
            error={error ?? (roster.isError ? curatorGroupsErrorText(roster.error, t) : null)}
            onTab={setTab}
            onQuery={setQuery}
            onToggle={(id) => setSelected((prev) => toggleSelected(prev, id))}
            onSelectAll={() => setSelected((prev) => selectAllFiltered(filtered, prev))}
            onClearSelection={() => setSelected(new Set<string>())}
            onShowMore={() => setShown((n) => n + PAGE)}
            onTargetGroup={setTargetGroupId}
            onAssign={startAssign}
            onRemove={startRemove}
          />
        </>
      )}

      <TakeoverDialog
        open={conflicts !== null}
        conflicts={conflicts ?? []}
        again={conflictAgain}
        pending={assign.isPending}
        onCancel={() => {
          setConflicts(null);
          setConflictAgain(false);
        }}
        onConfirm={() => assign.mutate({ ids: pendingIds, confirm: true })}
      />
    </div>
  );
}
